import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import axios from 'axios';
import { toast } from 'sonner';

const RespuestasFormulario = () => {
  const { formularioId } = useParams();
  const navigate = useNavigate();
  const { user, API } = useContext(AuthContext);
  const [formulario, setFormulario] = useState(null);
  const [respuestas, setRespuestas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [modalRespuesta, setModalRespuesta] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [fotoModal, setFotoModal] = useState(null);

  useEffect(() => {
    loadData();
  }, [formularioId]);

  const loadData = async () => {
    try {
      const isStudent = ['estudiante', 'pastor', 'graduado'].includes(user?.rol);
      
      if (isStudent) {
        // Students: use /mi-respuesta endpoint (only sees their own response)
        const [formularioRes, miRespuestaRes] = await Promise.all([
          axios.get(`${API}/formularios/${formularioId}`),
          axios.get(`${API}/formularios/${formularioId}/mi-respuesta`).catch(() => null)
        ]);
        
        setFormulario(formularioRes.data);
        if (miRespuestaRes?.data) {
          setRespuestas([miRespuestaRes.data]);
        } else {
          setRespuestas([]);
        }
      } else {
        // Admin/Profesor: use /respuestas endpoint (sees all responses)
        const [formularioRes, respuestasRes] = await Promise.all([
          axios.get(`${API}/formularios/${formularioId}`),
          axios.get(`${API}/formularios/${formularioId}/respuestas`)
        ]);
        
        setFormulario(formularioRes.data);
        setRespuestas(respuestasRes.data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar respuestas');
      navigate('/app/formularios-independientes');
    } finally {
      setLoading(false);
    }
  };

  const handleVerRespuesta = async (respuesta) => {
    const tieneImagenRecortada = respuesta.respuestas?.some(r => r.tiene_imagen || r.contenido_recortado);
    if (!tieneImagenRecortada) {
      setModalRespuesta(respuesta);
      return;
    }
    setLoadingDetalle(true);
    try {
      const res = await axios.get(`${API}/formularios/${formularioId}/respuestas/${respuesta.id}/detalle`);
      setModalRespuesta(res.data);
    } catch {
      setModalRespuesta(respuesta);
    } finally {
      setLoadingDetalle(false);
    }
  };


  const handleDeleteRespuesta = async (respuestaId, estudianteNombre) => {
    const confirmar = window.confirm(
      `¿Estás seguro de que deseas borrar la respuesta de ${estudianteNombre}?\n\n` +
      `Esta acción NO se puede deshacer.`
    );

    if (!confirmar) return;

    setDeleting(respuestaId);
    try {
      await axios.delete(`${API}/formularios/${formularioId}/respuestas/${respuestaId}`);
      toast.success(`Respuesta de ${estudianteNombre} eliminada`);
      setModalRespuesta(null);
      loadData();
    } catch (error) {
      console.error('Error deleting response:', error);
      toast.error('Error al borrar respuesta');
    } finally {
      setDeleting(null);
    }
  };

  // Detectar si una respuesta es una imagen (base64)
  const isImage = (respuesta) => {
    if (!respuesta) return false;
    return respuesta.startsWith('data:image/');
  };

  // Descargar imagen
  const downloadImage = (base64Data, fileName) => {
    const link = document.createElement('a');
    link.href = base64Data;
    link.download = fileName || 'foto.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Imprimir una respuesta
  const handlePrintRespuesta = (respuesta) => {
    const printWindow = window.open('', '_blank');
    const content = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Respuesta - ${respuesta.estudiante_nombre}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; }
          h1 { color: #1e40af; border-bottom: 2px solid #1e40af; padding-bottom: 10px; }
          .info { background: #f3f4f6; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
          .pregunta { margin: 20px 0; padding: 15px; border-left: 4px solid #3b82f6; background: #f9fafb; }
          .pregunta-titulo { font-weight: bold; color: #1f2937; margin-bottom: 10px; }
          .respuesta { background: white; padding: 10px; border-radius: 4px; }
          .foto { max-width: 300px; border-radius: 8px; margin: 10px 0; }
        </style>
      </head>
      <body>
        <h1>${formulario?.titulo || 'Formulario'}</h1>
        <div class="info">
          <strong>Nombre:</strong> ${respuesta.estudiante_nombre}<br>
          <strong>Email:</strong> ${respuesta.email || 'N/A'}<br>
          <strong>Teléfono:</strong> ${respuesta.telefono || 'N/A'}<br>
          <strong>Fecha:</strong> ${new Date(respuesta.fecha_envio).toLocaleString()}
        </div>
        ${respuesta.respuestas?.map((resp, i) => `
          <div class="pregunta">
            <div class="pregunta-titulo">${i + 1}. ${resp.pregunta_titulo?.replace(/<[^>]*>/g, '') || 'Pregunta'}</div>
            <div class="respuesta">
              ${isImage(resp.respuesta) 
                ? `<img src="${resp.respuesta}" class="foto" alt="Foto" />`
                : resp.respuesta || 'Sin respuesta'
              }
            </div>
          </div>
        `).join('') || ''}
      </body>
      </html>
    `;
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.print();
  };

  // Exportar todas las respuestas a CSV
  const handleExportCSV = () => {
    if (respuestas.length === 0) {
      toast.error('No hay respuestas para exportar');
      return;
    }

    const todasPreguntas = new Set();
    respuestas.forEach(r => {
      r.respuestas?.forEach(resp => {
        const titulo = resp.pregunta_titulo?.replace(/<[^>]*>/g, '') || 'Pregunta';
        todasPreguntas.add(titulo);
      });
    });

    const preguntasArray = Array.from(todasPreguntas);
    const headers = ['Nombre', 'Email', 'Teléfono', 'Fecha', ...preguntasArray];
    
    const rows = respuestas.map(r => {
      const row = [
        r.estudiante_nombre || '',
        r.email || '',
        r.telefono || '',
        new Date(r.fecha_envio).toLocaleString()
      ];
      
      preguntasArray.forEach(pregunta => {
        const resp = r.respuestas?.find(re => 
          (re.pregunta_titulo?.replace(/<[^>]*>/g, '') || '') === pregunta
        );
        let valor = resp?.respuesta || '';
        if (isImage(valor)) valor = '[IMAGEN ADJUNTA]';
        valor = `"${valor.replace(/"/g, '""')}"`;
        row.push(valor);
      });
      
      return row;
    });

    const csvContent = [
      headers.map(h => `"${h}"`).join(','),
      ...rows.map(r => r.join(','))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `respuestas_${formulario?.titulo || 'formulario'}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    toast.success('CSV exportado correctamente');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Cargando respuestas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <Button
          variant="ghost"
          onClick={() => navigate(`/app/formulario/${formularioId}/gestionar`)}
          className="mb-4 text-blue-600 hover:text-blue-800"
        >
          <i className="fas fa-arrow-left mr-2"></i>
          Volver a Gestionar Formulario
        </Button>
        
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Respuestas: {formulario?.titulo}
            </h1>
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <div className="flex items-center">
                <i className="fas fa-users mr-1"></i>
                {respuestas.length} respuestas
              </div>
            </div>
          </div>
          
          {respuestas.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={handleExportCSV}
                className="text-green-600 border-green-300 hover:bg-green-50"
              >
                <i className="fas fa-file-csv mr-2"></i>
                Exportar CSV
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Tarjetas de respuestas */}
      {respuestas.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {respuestas.map((respuesta, index) => (
            <Card 
              key={respuesta.id} 
              className="shadow-sm hover:shadow-md transition-shadow cursor-pointer border-l-4 border-l-blue-500"
              onClick={() => handleVerRespuesta(respuesta)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="fas fa-user text-blue-600"></i>
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-gray-900 truncate">
                          {respuesta.estudiante_nombre}
                        </h3>
                        <p className="text-xs text-gray-500 truncate">
                          {respuesta.email || 'Sin email'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      {respuesta.telefono && (
                        <span className="flex items-center gap-1">
                          <i className="fas fa-phone"></i>
                          {respuesta.telefono}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <i className="fas fa-calendar"></i>
                        {respuesta.fecha_envio && new Date(respuesta.fecha_envio).getFullYear() > 2000
                          ? new Date(respuesta.fecha_envio).toLocaleDateString()
                          : 'Sin fecha'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-blue-600 hover:bg-blue-50"
                      data-testid={`ver-respuesta-${respuesta.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleVerRespuesta(respuesta);
                      }}
                    >
                      <i className="fas fa-eye mr-1"></i>
                      Ver
                    </Button>
                    {user?.rol === 'admin' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-500 hover:bg-red-50 hover:text-red-700"
                        data-testid={`delete-respuesta-${respuesta.id}`}
                        disabled={deleting === respuesta.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteRespuesta(respuesta.id, respuesta.estudiante_nombre);
                        }}
                      >
                        {deleting === respuesta.id ? (
                          <i className="fas fa-spinner fa-spin"></i>
                        ) : (
                          <i className="fas fa-trash"></i>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
                
                {/* Preview de cantidad de respuestas */}
                <div className="mt-3 pt-3 border-t flex items-center justify-between text-xs text-gray-500">
                  <span>
                    <i className="fas fa-list-check mr-1"></i>
                    {respuesta.respuestas?.length || 0} respuestas
                  </span>
                  {respuesta.respuestas?.some(r => isImage(r.respuesta) || r.tiene_imagen) && (
                    <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-700">
                      <i className="fas fa-image mr-1"></i>
                      Con fotos
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="shadow-sm">
          <CardContent className="text-center py-12">
            <i className="fas fa-inbox text-gray-300 text-6xl mb-4"></i>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No hay respuestas aún
            </h3>
            <p className="text-gray-600">
              Aún no han respondido este formulario.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Modal de respuesta completa */}
      {modalRespuesta && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setModalRespuesta(null)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
                    <i className="fas fa-user text-2xl"></i>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">{modalRespuesta.estudiante_nombre}</h2>
                    <div className="flex flex-wrap gap-3 text-sm text-blue-100 mt-1">
                      {modalRespuesta.email && (
                        <span><i className="fas fa-envelope mr-1"></i>{modalRespuesta.email}</span>
                      )}
                      {modalRespuesta.telefono && (
                        <span><i className="fas fa-phone mr-1"></i>{modalRespuesta.telefono}</span>
                      )}
                    </div>
                    <p className="text-sm text-blue-200 mt-1">
                      <i className="fas fa-calendar mr-1"></i>
                      {new Date(modalRespuesta.fecha_envio).toLocaleString()}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setModalRespuesta(null)}
                  className="text-white/80 hover:text-white text-2xl"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>

            {/* Modal Body - Scrollable */}
            <div className="overflow-y-auto max-h-[calc(90vh-200px)] p-6">
              <div className="space-y-4">
                {modalRespuesta.respuestas?.map((resp, respIndex) => (
                  <div key={respIndex} className="border-l-4 border-blue-300 pl-4 py-2">
                    <div 
                      className="font-medium text-gray-900 mb-2 prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: resp.pregunta_titulo || `Pregunta ${respIndex + 1}` }}
                    />
                    
                    {isImage(resp.respuesta) ? (
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <img 
                          src={resp.respuesta} 
                          alt="Foto subida" 
                          className="max-w-full max-h-64 rounded-lg shadow-md cursor-pointer hover:opacity-90"
                          onClick={() => setFotoModal(resp.respuesta)}
                        />
                        <div className="flex gap-2 mt-3">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setFotoModal(resp.respuesta)}
                            className="text-blue-600"
                          >
                            <i className="fas fa-expand mr-1"></i>
                            Ampliar
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadImage(resp.respuesta, `foto_${modalRespuesta.estudiante_nombre}_${respIndex + 1}.png`)}
                            className="text-green-600"
                          >
                            <i className="fas fa-download mr-1"></i>
                            Descargar
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-700 bg-gray-50 p-3 rounded-lg whitespace-pre-wrap">
                        {resp.respuesta || <span className="text-gray-400 italic">Sin respuesta</span>}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="border-t bg-gray-50 p-4 flex flex-wrap gap-2 justify-between">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => handlePrintRespuesta(modalRespuesta)}
                  className="text-blue-600"
                >
                  <i className="fas fa-print mr-2"></i>
                  Imprimir
                </Button>
              </div>
              <div className="flex gap-2">
                {!['estudiante', 'pastor', 'graduado'].includes(user?.rol) && (
                <Button
                  variant="outline"
                  className="text-red-600 hover:bg-red-50"
                  onClick={() => handleDeleteRespuesta(modalRespuesta.id, modalRespuesta.estudiante_nombre)}
                  disabled={deleting === modalRespuesta.id}
                >
                  {deleting === modalRespuesta.id ? (
                    <><i className="fas fa-spinner fa-spin mr-2"></i>Borrando...</>
                  ) : (
                    <><i className="fas fa-trash mr-2"></i>Borrar</>
                  )}
                </Button>
                )}
                <Button onClick={() => setModalRespuesta(null)}>
                  Cerrar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal para ver foto en grande */}
      {fotoModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60] p-4"
          onClick={() => setFotoModal(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <button
              onClick={() => setFotoModal(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 text-xl"
            >
              <i className="fas fa-times mr-2"></i>Cerrar
            </button>
            <img 
              src={fotoModal} 
              alt="Foto ampliada" 
              className="max-w-full max-h-[85vh] rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="absolute bottom-4 right-4">
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  downloadImage(fotoModal, 'foto_descargada.png');
                }}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <i className="fas fa-download mr-2"></i>
                Descargar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Indicador de carga al abrir detalle */}
      {loadingDetalle && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-xl p-6 shadow-xl text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-3 font-medium">Cargando respuesta completa...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default RespuestasFormulario;
